'use client'

import { useInView } from 'react-intersection-observer'

import { type PortableTextBlock, stegaClean, toPlainText } from 'next-sanity'

import { Button, type ButtonProps, buttonSizeClasses } from '@/app/components/Button'
import { CheckListItem } from '@/app/components/CheckListItem'
import PortableText from '@/app/components/PortableText'
import PortableTextMinimal from '@/app/components/PortableTextMinimal'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn, ptGreenTextParagraphs } from '@/app/utils/style'
import { CtaSection as CtaSectionRaw } from '@/sanity.types'

type CtaSectionProps = Omit<CtaSectionRaw, 'buttons'> & {
  buttons?: Array<{ _key: string } & ButtonProps['button']>
}

type CtaProps = {
  block: CtaSectionProps & { _key?: string }
  index: number
  pageId?: string
  pageType?: string
  isLast?: boolean
}

function RenderCtaContent({
  block,
  pageId,
  pageType,
  className,
  iconColor,
}: {
  block: CtaSectionProps & { _key?: string }
  pageId?: string
  pageType?: string
  className?: string
  iconColor?: string
}) {
  const isListStyle = stegaClean(block.contentStyle) === 'list'
  const bgColor = stegaClean(block.bgColor) || 'green'

  if (isListStyle && block.listItems?.length) {
    return (
      <ul
        className={cn(
          'grid grid-cols-1 md:grid-cols-2 xl:flex xl:flex-row px-md sm:px-0 gap-2xl xl:gap-3xl [&_ul]:mt-l [&_ul]:ml-md [&_ul]:my-0 size-body-md self-stretch',
          className,
        )}
      >
        {block.listItems.map(
          (item) =>
            item.text && (
              <CheckListItem
                key={item._key}
                size="l"
                classNames={{
                  root: 'items-center font-normal text-green-50 xl:justify-center [&>span:last-child]:xl:flex-none',
                  icon: iconColor,
                }}
              >
                {toPlainText(item.text)}
              </CheckListItem>
            ),
        )}
      </ul>
    )
  }

  if (!isListStyle && block.content?.length) {
    return (
      <PortableText
        className={cn(className, bgColor === 'green' && ptGreenTextParagraphs)}
        tracking={{ location: TRACKING_LOCATIONS.CTA }}
        buttonClassName={buttonSizeClasses({ lg: 'md' })}
        value={block.content as PortableTextBlock[]}
        documentId={pageId}
        documentType={pageType}
        parentPath={block._key ? `pageBuilder[_key=="${block._key}"].content` : undefined}
      />
    )
  }

  return null
}

const backgroundImages = {
  color: '/images_pwf/cta_back_color.jpg',
  mono: '/images_pwf/cta_back_mono.jpg',
}

export default function CtaSection({ block, pageId, pageType }: CtaProps) {
  // Lazy load background image when section enters viewport
  const [inViewRef, inView] = useInView({
    rootMargin: '200px', // Load 200px before entering viewport
    threshold: 0,
    triggerOnce: true,
  })

  // Check if there's content to display
  const hasContent =
    (stegaClean(block.contentStyle) === 'list' && block.listItems?.length) ||
    (stegaClean(block.contentStyle) !== 'list' && block.content?.length)

  const backgroundVariant = stegaClean(block.backgroundVariant) || 'color'
  const backgroundImage = backgroundImages[backgroundVariant as keyof typeof backgroundImages]

  const bgColor = stegaClean(block.bgColor) || 'green'
  const bgColorClass = bgColor === 'purple' ? 'bg-purple-900' : 'bg-green-900'
  const iconColor = bgColor === 'purple' ? 'text-purple-400' : 'text-green-200'

  return (
    <div ref={inViewRef} className={cn('content-container', 'my-xl md:my-10xl')}>
      <div
        className="mx-auto max-w-[1144px] p-xl lg:p-6xl rounded-md"
        style={
          inView
            ? {
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      >
        <div
          className={cn(
            'flex flex-col items-center gap-7xl self-stretch rounded-md py-8xl px-2xl lg:py-10xl',
            bgColorClass,
          )}
        >
          <div className="flex flex-col gap-8xl lg:gap-5xl">
            <div className={cn('flex flex-col gap-4xl lg:gap-2xl', !hasContent && 'shrink-0')}>
              {block.heading?.length && (
                <p className="size-header-h4 !font-normal md:!font-medium text-center text-white-default">
                  <PortableTextMinimal
                    value={block.heading as PortableTextBlock[]}
                    tracking={{ location: TRACKING_LOCATIONS.CTA }}
                  />
                </p>
              )}
              <RenderCtaContent
                block={block}
                pageId={pageId}
                pageType={pageType}
                className="grid"
                iconColor={iconColor}
              />
            </div>
            {block.buttons?.length && (
              <div
                className={cn(
                  'flex flex-col gap-xl w-full md:w-auto mx-auto items-center justify-center sm:flex-row',
                )}
              >
                {block.buttons.map((button) => (
                  <Button
                    key={button._key}
                    button={button}
                    tracking={{ location: TRACKING_LOCATIONS.CTA }}
                    className={cn('w-full md:w-auto ', buttonSizeClasses({ lg: 'md' }))}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
